import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Star, Gift, Info } from 'lucide-react';

interface ProductCardProps {
  product: {
    id: number;
    name: string;
    description: string;
    price: number;
    image: string;
    rating: number;
    category: string;
  };
  points: number;
  onUsePoints?: (points: number) => void;
}

export function ProductCard({ product, points, onUsePoints }: ProductCardProps) {
  const [usePoints, setUsePoints] = useState(false);

  // Calculate maximum possible discount
  const maxDiscountPercent = 30;
  const maxDiscountAmount = 1200;
  const pointValue = 0.1; // Each point is worth ₹0.1
  
  // Calculate potential discount based on available points
  const potentialDiscount = Math.min(
    points * pointValue,
    product.price * (maxDiscountPercent / 100),
    maxDiscountAmount
  );

  const pointsNeeded = Math.ceil(potentialDiscount / pointValue);
  const finalPrice = usePoints ? product.price - potentialDiscount : product.price;
  const discountPercent = Math.round((potentialDiscount / product.price) * 100);

  return (
    <motion.div
      whileHover={{ scale: 1.02 }}
      className="theme-card relative overflow-hidden group"
    >
      {/* Product Image */}
      <div className="relative h-48 overflow-hidden">
        <img
          src={product.image}
          alt={product.name}
          className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-500"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-background via-background/20 to-transparent opacity-60" />
      </div>

      {/* Content */}
      <div className="p-4 space-y-4">
        <div>
          <div className="flex items-center justify-between">
            <h3 className="font-medium">{product.name}</h3>
            <div className="flex items-center gap-1">
              <Star className="w-4 h-4 text-amber-500" />
              <span className="text-sm">{product.rating}</span>
            </div>
          </div>
          <p className="text-sm text-muted-foreground line-clamp-2">{product.description}</p>
        </div>

        {/* Points Discount Section */}
        {points > 0 && (
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <Gift className="w-4 h-4 text-primary" />
              <span className="text-sm font-medium">Use Points for Discount</span>
            </div>
            
            <div className="flex items-center gap-2">
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={usePoints}
                  onChange={() => setUsePoints(!usePoints)}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-muted peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
              </label>
              <span className="text-sm text-muted-foreground">
                Use {pointsNeeded} points to save ₹{potentialDiscount}
              </span>
            </div>

            {usePoints && (
              <div className="flex items-start gap-2 text-xs bg-primary/10 text-primary p-2 rounded-lg">
                <Info className="w-4 h-4 shrink-0 mt-0.5" />
                <p>Save {discountPercent}% ({pointsNeeded} points) on your purchase</p>
              </div>
            )}
          </div>
        )}

        {/* Price Display */}
        <div className="flex items-end justify-between">
          <div>
            {usePoints && (
              <div className="text-sm text-muted-foreground line-through">
                ₹{product.price}
              </div>
            )}
            <div className="text-lg font-bold">
              ₹{Math.round(finalPrice)}
            </div>
          </div>
          <button className="theme-button px-4 py-2 rounded-lg text-sm">
            Add to Cart
          </button>
        </div>
      </div>
    </motion.div>
  );
}